import React, { useContext, useState } from 'react';
import Header from '../components/Header';
import { Box, Button, Container, Grid, Typography, Divider, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TablePagination, Snackbar, Alert, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import { findAllValidBanners, saveBannerConfig, deleteBannerConfig, findBannerConfigForFilter } from '../../actions/uiActions';
import dayjs from 'dayjs';
import Tooltip from '@mui/material/Tooltip';
import { CompanyContext } from '../../CompanyProvider';
import { LoadingContext } from '../../LoadingContext';


function BannersPage({handleLogout}) {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bannerStatus, setBannerStatus] = useState('Valid'); // New state for status
  const [validFrom, setValidFrom] = useState(null);
  const [validTo, setValidTo] = useState(null);
  const [banners, setBanners] = useState([]); // Should be loaded from backend
  const [openDialog, setOpenDialog] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [newBanner, setNewBanner] = useState({
    bannerID: '',
    primaryText: '',
    validFrom: null,
    validTo: null,
    action: '',
    image: null,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { setLoading, setError } = useContext(LoadingContext);

  const companyCode = useContext(CompanyContext);
  React.useEffect(() => {
    async function fetchBanners() {
      setLoading(true);
      try {
        // You may want to update this to fetch by status on initial load
        const data = await findAllValidBanners(companyCode);
        setBanners(data);
      } catch (e) {
        setBanners([]);
      } finally {
        setLoading(false);
      }
    }
    fetchBanners();
  }, []);

  // Update handleList to use bannerStatus
  const handleList = () => {
    async function fetchBanners() {
      try {
        // Pass bannerStatus to your backend filter function
        const data = await findBannerConfigForFilter(companyCode, bannerStatus);
        setBanners(data);
      } catch (e) {
        setBanners([]);
      }
    }
    fetchBanners();
  };

  // Handler for image upload (same as InventoryPage)
  const handleBannerImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setNewBanner({ ...newBanner, image: e.target.files[0] });
    }
  };

  // Handler for dialog open
  const handleOpenNewBannerDialog = () => {
    setNewBanner({
      bannerID: '',
      primaryText: '',
      validFrom: null,
      validTo: null,
      action: '',
      image: null,
    });
    setOpenDialog(true);
  };

  // Handler for dialog close
  const handleCloseNewBannerDialog = () => {
    setOpenDialog(false);
  };

  // Save new banner handler
  const handleSaveBanner = async () => {
    setLoading(true);
    try {
      await saveBannerConfig(companyCode, {
        ...newBanner,
        validFrom: newBanner.validFrom ? newBanner.validFrom.toISOString() : null,
        validTo: newBanner.validTo ? newBanner.validTo.toISOString() : null,
        image: undefined // don't send image in JSON part
      }, newBanner.image);
      setOpenDialog(false);
      setSnackbar({ open: true, message: 'Banner saved successfully!', severity: 'success' });
      // Refresh banners list
      const data = await findAllValidBanners(companyCode);
      setBanners(data);
    } catch (e) {
      setError('Failed to save banner');
      setSnackbar({ open: true, message: 'Failed to save banner', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Delete banner handler
  const handleDeleteBanner = async (bannerID) => {
    try {
      await deleteBannerConfig(companyCode, bannerID);
      setSnackbar({ open: true, message: 'Banner deleted successfully!', severity: 'success' });
      // Refresh banners list
      const data = await findAllValidBanners(companyCode);
      setBanners(data);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to delete banner', severity: 'error' });
    }
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginated banners
  const paginatedBanners = banners.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
      <Box sx={{ minHeight: '100vh', background: '#f1f5f9' }}>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Header handleLogout={handleLogout}/>
      {/* Snackbar for feedback */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Box sx={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <Container
          sx={{
            pt: 4,
            pb: 4,
            mt: -1.5,
            background: '#ffffff',
            border: '1px solid #e8edf5',
            borderRadius: 4,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 20px rgba(0,0,0,0.04)',
            
            minWidth: "95%",
            minHeight: '80vh',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1a3c6e', mb: 1.5, mt: 0, letterSpacing: 0.5, fontSize: 20, textAlign: 'left' }}>
            Banners
          </Typography>
          <Divider sx={{ mb: 2, borderColor: '#e2e8f0' }} />
          {/* Filter Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4} md={6} sx={{width:"20%"}}>
              <FormControl fullWidth size="small">
                <InputLabel id="banner-status-label">Banner Status</InputLabel>
                <Select
                  labelId="banner-status-label"
                  id="banner-status"
                  value={bannerStatus}
                  label="Banner Status"
                  onChange={e => setBannerStatus(e.target.value)}
                >
                  <MenuItem value="Valid">Valid</MenuItem>
                  <MenuItem value="Expired">Expired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Button variant="contained" color="primary" sx={{ minWidth: 120, background: '#1a3c6e', '&:hover': { background: '#17406a' } }} onClick={handleList}>
                List
              </Button>
            </Grid>
            <Grid item xs={12} sm={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button variant="outlined" color="primary" sx={{ minWidth: 160, borderColor: '#1a3c6e', color: '#1a3c6e' }} startIcon={<AddPhotoAlternateIcon />} onClick={handleOpenNewBannerDialog}>
                + New Banner
              </Button>
            </Grid>
          </Grid>
          {/* Details Section - Table with Pagination */}
          <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <TablePagination
              component="div"
              count={banners.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                border: 'none',
                maxWidth: 400,
                ml: 0,
                mb: 1,
                mt: 0,
                fontWeight: 500
              }}
            />
          </Box>
          <TableContainer component={Paper} sx={{ borderRadius: 2, background: '#ffffff', border: '1px solid #e8edf5', width: '100%', mx: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Banner ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Text</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valid From</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valid To</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Image</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Click Action</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(paginatedBanners.length > 0 ? paginatedBanners : []).map((banner) => (
                  <TableRow key={banner.bannerID}>
                    <TableCell>{banner.bannerID}</TableCell>
                    <TableCell>
                      <Tooltip title={banner.primaryText || ''} arrow placement="top">
                        <span>
                          {banner.primaryText && banner.primaryText.length > 30
                            ? banner.primaryText.slice(0, 30) + '...'
                            : banner.primaryText}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{banner.validFrom ? dayjs(banner.validFrom).format('YYYY-MM-DD') : ''}</TableCell>
                    <TableCell>{banner.validTo ? dayjs(banner.validTo).format('YYYY-MM-DD') : ''}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => setPreviewImage(banner.imageURL)} size="small">
                      <ImageIcon sx={{ color: '#1a3c6e', fontSize: 28 }} />
                    </IconButton>
                  </TableCell>
                    <TableCell>{banner.action ? banner.action : '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" sx={{ color: '#1a3c6e' }}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" size="small" onClick={() => handleDeleteBanner(banner.bannerID)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Image Preview Dialog */}
          <Dialog open={!!previewImage} onClose={() => setPreviewImage(null)} maxWidth="xs">
            <DialogTitle sx={{ color: '#1a3c6e', fontWeight: 700 }}>Banner Image Preview</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
                {previewImage && <img src={previewImage} alt="Banner Preview" style={{ maxWidth: 320, maxHeight: 180, borderRadius: 8, border: '1px solid #90caf9' }} />}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPreviewImage(null)} color="primary">Close</Button>
            </DialogActions>
          </Dialog>
          {/* New Banner Dialog */}
          <Dialog open={openDialog} onClose={handleCloseNewBannerDialog} maxWidth="sm" fullWidth PaperProps={{
            sx: {
              background: '#ffffff',
              border: 'none',
              borderRadius: '16px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
            }
          }}>
            <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', letterSpacing: 0, fontSize: 18, textAlign: 'left', background: '#ffffff', borderBottom: '1px solid #e8edf5' }}>
              Add New Banner
            </DialogTitle>
            <DialogContent dividers sx={{ background: '#ffffff' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} sx={{width:"50%"}}>
                  <TextField label="Banner ID" value={newBanner.bannerID} onChange={e => setNewBanner({ ...newBanner, bannerID: e.target.value })} fullWidth required size="small" sx={{ background: '#f8fafc', borderRadius: 2 }} />
                </Grid>
                <Grid item xs={12} sx={{width:"100%"}}>
                  <TextField label="Text" value={newBanner.primaryText} onChange={e => setNewBanner({ ...newBanner, primaryText: e.target.value })} fullWidth required size="small" sx={{ background: '#f8fafc', borderRadius: 2 }} />
                </Grid>
                
                <Grid item xs={12} sm={6} sx={{width:"49%"}}>
                  <DatePicker
                    label="Valid From"
                    value={newBanner.validFrom}
                    onChange={date => setNewBanner({ ...newBanner, validFrom: date })}
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: { background: '#f8fafc', borderRadius: 2 } } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{width:"48%"}}>
                  <DatePicker
                    label="Valid To"
                    value={newBanner.validTo}
                    onChange={date => setNewBanner({ ...newBanner, validTo: date })}
                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: { background: '#f8fafc', borderRadius: 2 } } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{width:"50%"}}>
                  <TextField label="Click Action" value={newBanner.action} onChange={e => setNewBanner({ ...newBanner, action: e.target.value })} fullWidth size="small" sx={{ background: '#f8fafc', borderRadius: 2 }} />
                </Grid>
                <Grid item xs={12} sx={{width:"98%"}}>
                  <Box
                    sx={{
                      border: '2px dashed #cbd5e1',
                      borderRadius: 2,
                      background: '#f8fafc',
                      p: 2,
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => document.getElementById('banner-image-upload').click()}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      e.preventDefault();
                      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                        setNewBanner({ ...newBanner, image: e.dataTransfer.files[0] });
                      }
                    }}
                  >
                    <input
                      id="banner-image-upload"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleBannerImageChange}
                    />
                    <ImageIcon sx={{ fontSize: 48, color: '#1a3c6e', mb: 1 }} />
                    <Typography sx={{ color: '#1a3c6e' }}>Drag and Drop the file</Typography>
                    <Typography sx={{ fontWeight: 600, my: 1, color: '#1a3c6e' }}>or</Typography>
                    <Button variant="contained" sx={{ background: '#1a3c6e', color: '#fff', borderRadius: 2, px: 3, textTransform: 'none', '&:hover': { background: '#17406a' } }} onClick={e => { e.stopPropagation(); document.getElementById('banner-image-upload').click(); }}>
                      Upload Image
                    </Button>
                    {newBanner.image && (
                      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 13, color: '#1976d2' }}>{newBanner.image.name}</Typography>
                        <Box sx={{ mt: 1 }}>
                          <img
                            src={URL.createObjectURL(newBanner.image)}
                            alt="preview"
                            style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8, border: '1px solid #90caf9' }}
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ background: '#ffffff', borderTop: '1px solid #e8edf5', pb: 2, pt: 2 }}>
              <Button onClick={handleCloseNewBannerDialog} color="secondary" sx={{ minWidth: 120, borderColor: '#1a3c6e', color: '#1a3c6e' }}>Cancel</Button>
              <Button color="primary" variant="contained" sx={{ minWidth: 120, background: '#1a3c6e', '&:hover': { background: '#17406a' } }} onClick={handleSaveBanner}>Save</Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </LocalizationProvider>
    </Box>
  );
}

export default BannersPage;
